import { matchRoutes, useLocation } from "react-router-dom";
import { NavigationExpandModes } from "../../types";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { isUndefined } from "lodash";
import { UclRouteObject } from "../../../../../utils/routes";

export interface NavigationTreeViewNode {
  id: string;
  parentId?: string;
  name: string;
  icon?: IconDefinition;
  expanded: boolean;
  path?: string;
  tooltipText?: string;
  isSelected: boolean;
  visible: boolean;
  fields?: NavigationTreeViewNode[];
}

export const useNavigationTreeViewConfig = () => {
  const location = useLocation();

  const generateNavigationTreeViewConfigNodes = (
    uclRouteObject: UclRouteObject[],
    parentPath?: string,
    parentRelativePath?: string
  ): NavigationTreeViewNode[] => {
    const componentTreeViewConfig: NavigationTreeViewNode[] = [];
    uclRouteObject
      .filter((x) => x.isAvailable)
      .forEach((route) => {
        const relativePath = `${parentRelativePath ?? ""}/${
          route.path
        }`.replace("//", "/");
        if (route.children && route.children) {
          const children = generateNavigationTreeViewConfigNodes(
            route.children,
            route.path,
            relativePath
          );
          componentTreeViewConfig.push({
            id: String(route.path),
            parentId: parentPath,
            name: route.label,
            icon: route.icon,
            path: relativePath,
            expanded: true,
            visible: isUndefined(route.showInNavigation)
              ? true
              : route.showInNavigation,
            isSelected:
              matchRoutes([{ path: relativePath }], location) !== null,
            fields: children,
          });
        } else {
          componentTreeViewConfig.push({
            id: String(route.path),
            parentId: parentPath,
            name: route.label,
            icon: route.icon,
            path: relativePath,
            expanded: true,
            visible: isUndefined(route.showInNavigation)
              ? true
              : route.showInNavigation,
            isSelected:
              matchRoutes([{ path: relativePath }], location) !== null,
          });
        }
      });
    return componentTreeViewConfig;
  };

  const generateNavigationTreeViewConfig = (
    uclRouteObject: UclRouteObject[],
    navigationExpandMode: NavigationExpandModes
  ): NavigationTreeViewNode[] => {
    let nodes = generateNavigationTreeViewConfigNodes(uclRouteObject);

    // Select Parent if Children Selected but showNavigation is false
    const rootSelected = nodes.find((x) => x.isSelected);
    if (!rootSelected) {
      const fields = nodes.map((x) => x.fields).flat();
      if (fields && fields.length > 0) {
        const selected = fields.find((x) => x?.isSelected);
        if (selected && !selected.visible) {
          nodes
            .filter((x) => x.id === selected.id)
            .forEach((x) => (x.isSelected = false));

          nodes
            .filter((x) => x.id === selected.parentId)
            .forEach((x) => (x.isSelected = true));
        }
      }
    }

    // Remove not visible nodes
    nodes = nodes.filter((x) => x.visible);
    nodes.forEach((x) => {
      if (x.fields && x.fields.length > 0) {
        x.fields = x.fields?.filter((y) => y.visible);
        // Support up to 2 levels to support Flyouts
        x.fields.forEach((y) => {
          if (y.fields && y.fields.length > 0) {
            y.fields = y.fields?.filter((z) => z.visible);
          }
        });
      }
    });

    // Our Nav Treeview just supports one nav level, so we just need to check the top level nodes
    if (navigationExpandMode === NavigationExpandModes.AllOpen) {
      nodes.filter((x) => !x.parentId).forEach((x) => (x.expanded = true));
    } else if (navigationExpandMode === NavigationExpandModes.AllClosed) {
      nodes.filter((x) => !x.parentId).forEach((x) => (x.expanded = false));
    } else {
      nodes.filter((x) => !x.parentId).forEach((x) => (x.expanded = false));

      const rootSelected = nodes.find((x) => x.isSelected);
      if (rootSelected) {
        nodes
          .filter((x) => x.id === rootSelected.id)
          .forEach((x) => (x.expanded = true));
      } else {
        const fields = nodes.map((x) => x.fields).flat();
        if (fields && fields.length > 0) {
          const selected = fields.find((x) => x?.isSelected);
          if (selected) {
            nodes
              .filter((x) => x.id === selected.parentId)
              .forEach((x) => (x.expanded = true));
          }
        }
      }
    }

    return nodes;
  };

  return { generateNavigationTreeViewConfig };
};
